import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FaArrowLeft, FaMoneyBillAlt, FaPlus, FaTrash, FaCar, FaShoppingCart, FaFileInvoice, FaUser, FaCalendarAlt, FaFilter, FaTimes, FaCheckCircle, FaExclamationCircle, FaChartLine } from 'react-icons/fa';

export default function Egresos({ sucursalId, user, goBack, modoAlquiler }) {
    const [egresos, setEgresos] = useState([]);
    const [vehiculos, setVehiculos] = useState([]); 
    const [insumos, setInsumos] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [filtroTipo, setFiltroTipo] = useState('todos'); // todos, compras, gastos
    const mesActualBO = new Date().toLocaleDateString('en-CA', { timeZone: 'America/La_Paz' }).slice(0, 7);
    const [mesSel, setMesSel] = useState(mesActualBO);
    const [limite, setLimite] = useState(25);
    useEffect(() => { setLimite(25); }, [mesSel, filtroTipo]);
    
    // Switchs
    const [esCompra, setEsCompra] = useState(false); 
    const [destinoCompra, setDestinoCompra] = useState('inventario'); // 'inventario' | 'insumos'
    const [busquedaInsumo, setBusquedaInsumo] = useState('');
    const [insumoSel, setInsumoSel] = useState(null);
    const isAlquilacar = modoAlquiler === true || parseInt(sucursalId) === 4; 

    const [form, setForm] = useState({ descripcion: '', monto: '', vehiculo_id: '', insumo_id: '', cantidad_comprada: '' });

    useEffect(() => {
        fetchEgresos();
        if (isAlquilacar) fetchVehiculos();
    }, [sucursalId]);

    useEffect(() => {
        if (esCompra) fetchInsumos();
    }, [esCompra, destinoCompra]);

    useEffect(() => {
        if (!showModal) { setBusquedaInsumo(''); setInsumoSel(null); setDestinoCompra('inventario'); }
    }, [showModal]);

    const fetchEgresos = async () => {
        try {
            const res = await axios.get(`https://api.alquilacarbolivia.com/api/egresos/${sucursalId}`);
            setEgresos(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchVehiculos = async () => {
        try {
            const res = await axios.get('https://api.alquilacarbolivia.com/api/vehiculos');
            setVehiculos(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchInsumos = async () => {
        try {
            if (destinoCompra === 'insumos') {
                const res = await axios.get(`https://api.alquilacarbolivia.com/api/insumos/${sucursalId}`);
                setInsumos(res.data.map(r => ({ id: r.id, nombre_insumo: r.nombre, cantidad: r.stock_almacen, unidad_medida: r.unidad_medida })));
            } else {
                const res = await axios.get(`https://api.alquilacarbolivia.com/api/inventario/${sucursalId}`);
                setInsumos(res.data);
            }
        } catch (error) { console.error(error); }
    };

    const limpiarInsumo = () => {
        setInsumoSel(null);
        setBusquedaInsumo('');
        setForm(f => ({ ...f, insumo_id: '', descripcion: '' }));
    };

    const cambiarDestino = (d) => {
        setDestinoCompra(d);
        limpiarInsumo();
        setInsumos([]);
    };

    const seleccionarInsumo = (ins) => {
        setInsumoSel(ins);
        setBusquedaInsumo('');
        setForm(f => ({ ...f, insumo_id: ins.id, descripcion: `Compra ${ins.nombre_insumo}` }));
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!form.monto || form.monto <= 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Monto inválido',
                text: 'Ingresa un monto mayor a 0',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        if (esCompra && (!form.insumo_id || !form.cantidad_comprada)) {
            Swal.fire({
                icon: 'warning',
                title: 'Datos incompletos',
                text: 'Selecciona un insumo y especifica la cantidad',
                confirmButtonColor: '#f59e0b'
            });
            return;
        }

        const result = await Swal.fire({
            title: '¿Confirmar registro?',
            html: `Se registrará un egreso de <strong>Bs. ${form.monto}</strong>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, registrar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            await axios.post('https://api.alquilacarbolivia.com/api/egresos', {
                sucursal_id: sucursalId,
                usuario_id: user.id,
                descripcion: esCompra ? `COMPRA: ${form.descripcion}` : form.descripcion,
                monto: form.monto,
                vehiculo_id: form.vehiculo_id || null,
                insumo_id: esCompra && destinoCompra === 'inventario' ? form.insumo_id : null,
                cantidad_comprada: esCompra ? form.cantidad_comprada : null,
                insumo_almacen_id: esCompra && destinoCompra === 'insumos' ? form.insumo_id : null
            });
            Swal.fire({
                icon: 'success',
                title: '¡Registrado!',
                text: 'Gasto guardado correctamente',
                confirmButtonColor: '#10b981'
            });
            setShowModal(false);
            setForm({ descripcion: '', monto: '', vehiculo_id: '', insumo_id: '', cantidad_comprada: '' });
            setEsCompra(false);
            fetchEgresos();
        } catch (error) { 
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'No se pudo guardar el gasto',
                confirmButtonColor: '#ef4444'
            });
        }
    };

    const handleDelete = async (id, descripcion, monto) => {
        const result = await Swal.fire({
            title: '¿Eliminar gasto?',
            html: `Se eliminará: <strong>${descripcion}</strong><br/>Monto: Bs. ${monto}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ef4444',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Sí, eliminar',
            cancelButtonText: 'Cancelar'
        });
        
        if (result.isConfirmed) {
            try {
                await axios.delete(`https://api.alquilacarbolivia.com/api/egresos/${id}`);
                Swal.fire('Eliminado', 'Gasto eliminado correctamente', 'success');
                fetchEgresos();
            } catch (error) {
                Swal.fire('Error', 'No se pudo eliminar', 'error');
            }
        }
    };

    // ---- Filtro por mes (hora de Bolivia) ----
    const mesDe = (fecha) => new Date(fecha).toLocaleDateString('en-CA', { timeZone: 'America/La_Paz' }).slice(0, 7);
    const NOMBRES_MES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const [anioSel, mesNum] = mesSel.split('-').map(Number);
    const tituloMes = `${NOMBRES_MES[mesNum - 1]} ${anioSel}`;
    const moverMes = (delta) => {
        const d = new Date(anioSel, mesNum - 1 + delta, 1);
        const nuevo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        if (nuevo > mesActualBO) return;
        setMesSel(nuevo);
    };
    const egresosMes = egresos.filter(e => mesDe(e.fecha) === mesSel);

    // Calcular estadísticas (solo del mes seleccionado)
    const calcularEstadisticas = () => {
        const totalEgresos = egresosMes.reduce((sum, e) => sum + parseFloat(e.monto), 0);
        const compras = egresosMes.filter(e => e.descripcion.includes('COMPRA'));
        const totalCompras = compras.reduce((sum, e) => sum + parseFloat(e.monto), 0);
        const gastosOperativos = egresosMes.filter(e => !e.descripcion.includes('COMPRA'));
        const totalGastosOperativos = gastosOperativos.reduce((sum, e) => sum + parseFloat(e.monto), 0);

        return { totalEgresos, totalCompras, totalGastosOperativos, cantidadEgresos: egresosMes.length };
    };

    const filtrarEgresos = () => {
        switch(filtroTipo) {
            case 'compras':
                return egresosMes.filter(e => e.descripcion.includes('COMPRA'));
            case 'gastos':
                return egresosMes.filter(e => !e.descripcion.includes('COMPRA'));
            default:
                return egresosMes;
        }
    };

    const stats = calcularEstadisticas();
    const egresosFiltrados = filtrarEgresos();
    const egresosVisibles = egresosFiltrados.slice(0, limite);

    return (
        <div className="container-fluid p-0" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            {/* Header Mejorado */}
            <div style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                padding: '1.5rem 2rem',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}>
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3">
                        <button 
                            className="btn btn-light shadow-sm" 
                            onClick={goBack}
                            style={{ 
                                borderRadius: '12px',
                                padding: '0.5rem 1rem',
                                fontWeight: '600',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={e => e.target.style.transform = 'translateY(-2px)'}
                            onMouseLeave={e => e.target.style.transform = 'translateY(0)'}
                        >
                            <FaArrowLeft className="me-2"/> Volver
                        </button>
                        <div className="text-white">
                            <h3 className="m-0 fw-bold d-flex align-items-center gap-2">
                                <FaMoneyBillAlt style={{ fontSize: '1.5rem' }}/> 
                                Gastos y Egresos
                            </h3>
                            <p className="m-0 opacity-75" style={{ fontSize: '0.9rem' }}>
                                Control de salidas de dinero
                            </p>
                        </div>
                    </div>
                    <button 
                        className="btn btn-light shadow-lg fw-bold"
                        onClick={() => setShowModal(true)}
                        style={{
                            borderRadius: '12px',
                            padding: '0.75rem 1.5rem',
                            background: 'white',
                            border: 'none',
                            color: '#dc2626',
                            transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={e => {
                            e.target.style.transform = 'translateY(-3px)';
                            e.target.style.boxShadow = '0 10px 20px rgba(220, 38, 38, 0.3)';
                        }}
                        onMouseLeave={e => {
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                        }}
                    >
                        <FaPlus className="me-2"/> Registrar Gasto
                    </button>
                </div>
            </div>

            <div className="p-4">
                {/* Tarjetas de estadísticas */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                    <div className="d-flex align-items-center gap-2">
                        <button type="button" className="btn btn-outline-secondary" style={{ borderRadius: '10px' }} onClick={() => moverMes(-1)}>‹</button>
                        <div className="fw-bold text-center" style={{ minWidth: '180px', fontSize: '1.15rem' }}>📅 {tituloMes}</div>
                        <button type="button" className="btn btn-outline-secondary" style={{ borderRadius: '10px' }} disabled={mesSel >= mesActualBO} onClick={() => moverMes(1)}>›</button>
                        {mesSel !== mesActualBO && (
                            <button type="button" className="btn btn-sm btn-outline-danger" style={{ borderRadius: '20px' }} onClick={() => setMesSel(mesActualBO)}>Volver al mes actual</button>
                        )}
                    </div>
                    <small className="text-muted">Las tarjetas y el historial muestran solo este mes</small>
                </div>
                <div className="row g-3 mb-4">
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Total Egresos</p>
                                        <h4 className="m-0 fw-bold">Bs. {stats.totalEgresos.toFixed(2)}</h4>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaMoneyBillAlt/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">En Compras</p>
                                        <h4 className="m-0 fw-bold">Bs. {stats.totalCompras.toFixed(2)}</h4>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaShoppingCart/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Gastos Operativos</p>
                                        <h4 className="m-0 fw-bold">Bs. {stats.totalGastosOperativos.toFixed(2)}</h4>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaFileInvoice/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card border-0 shadow-sm" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
                            <div className="card-body p-3 text-white">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="m-0 opacity-75 small">Total Registros</p>
                                        <h4 className="m-0 fw-bold">{stats.cantidadEgresos}</h4>
                                    </div>
                                    <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>
                                        <FaChartLine/>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filtros y tabla */}
                <div className="card border-0 shadow-sm" style={{ borderRadius: '16px' }}>
                    <div className="card-header bg-white border-0 p-4">
                        <div className="d-flex justify-content-between align-items-center">
                            <h5 className="m-0 fw-bold d-flex align-items-center gap-2">
                                <FaFileInvoice style={{ color: '#dc2626' }}/> Historial de Egresos
                            </h5>
                            
                            {/* Filtros */}
                            <div className="btn-group" role="group">
                                <button 
                                    type="button" 
                                    className={`btn ${filtroTipo === 'todos' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => setFiltroTipo('todos')}
                                    style={{ borderRadius: '10px 0 0 10px' }}
                                >
                                    Todos
                                </button>
                                <button 
                                    type="button" 
                                    className={`btn ${filtroTipo === 'compras' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => setFiltroTipo('compras')}
                                >
                                    <FaShoppingCart className="me-1"/> Compras
                                </button>
                                <button 
                                    type="button" 
                                    className={`btn ${filtroTipo === 'gastos' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    onClick={() => setFiltroTipo('gastos')}
                                    style={{ borderRadius: '0 10px 10px 0' }}
                                >
                                    <FaFileInvoice className="me-1"/> Gastos
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="card-body p-0">
                        {egresosFiltrados.length === 0 ? (
                            <div className="text-center py-5">
                                <div style={{ fontSize: '4rem', color: '#cbd5e1' }}>
                                    <FaMoneyBillAlt/>
                                </div>
                                <h5 className="text-muted mt-3">No hay egresos registrados</h5>
                                <p className="text-muted">
                                    {filtroTipo === 'todos' 
                                        ? 'Comienza registrando gastos y compras'
                                        : 'No hay registros para el filtro seleccionado'
                                    }
                                </p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <table className="table table-hover align-middle mb-0">
                                    <thead style={{ 
                                        background: '#f8fafc', 
                                        borderBottom: '2px solid #e2e8f0'
                                    }}>
                                        <tr>
                                            <th className="fw-bold text-muted" style={{ padding: '1rem' }}>FECHA</th>
                                            <th className="fw-bold text-muted" style={{ padding: '1rem' }}>DESCRIPCIÓN</th>
                                            {isAlquilacar && <th className="fw-bold text-muted" style={{ padding: '1rem' }}>VEHÍCULO</th>}
                                            <th className="fw-bold text-muted" style={{ padding: '1rem' }}>USUARIO</th>
                                            <th className="fw-bold text-muted text-end" style={{ padding: '1rem' }}>MONTO</th>
                                            <th className="fw-bold text-muted text-center" style={{ padding: '1rem' }}>ACCIÓN</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {egresosVisibles.map(e => {
                                            const esCompraItem = e.descripcion.includes('COMPRA');
                                            return (
                                                <tr key={e.id}>
                                                    <td style={{ padding: '1rem' }}>
                                                        <div>
                                                            <div className="fw-semibold">
                                                                {new Date(e.fecha).toLocaleDateString('es-BO', { 
                                                                    day: '2-digit', 
                                                                    month: 'short', 
                                                                    year: 'numeric' 
                                                                })}
                                                            </div>
                                                            <div className="text-muted small">
                                                                {new Date(e.fecha).toLocaleTimeString('es-BO', { 
                                                                    hour: '2-digit', 
                                                                    minute: '2-digit' 
                                                                })}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: '1rem' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '10px',
                                                                background: esCompraItem 
                                                                    ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                                                                    : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontSize: '1.1rem'
                                                            }}>
                                                                {esCompraItem ? <FaShoppingCart/> : <FaFileInvoice/>}
                                                            </div>
                                                            <div>
                                                                <div className="fw-bold">{e.descripcion}</div>
                                                                <span 
                                                                    className="badge"
                                                                    style={{
                                                                        background: esCompraItem ? '#fef3c7' : '#ede9fe',
                                                                        color: esCompraItem ? '#92400e' : '#5b21b6',
                                                                        fontSize: '0.75rem',
                                                                        padding: '0.25rem 0.5rem'
                                                                    }}
                                                                >
                                                                    {esCompraItem ? 'Compra' : 'Gasto Operativo'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {isAlquilacar && (
                                                        <td style={{ padding: '1rem' }}>
                                                            {e.marca ? (
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <FaCar style={{ color: '#6b7280' }}/>
                                                                    <span className="fw-semibold">{e.marca} {e.modelo}</span>
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td style={{ padding: '1rem' }}>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div style={{
                                                                width: '32px',
                                                                height: '32px',
                                                                borderRadius: '50%',
                                                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.75rem'
                                                            }}>
                                                                {e.usuario ? e.usuario.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                            <span className="fw-semibold small">{e.usuario || 'Sistema'}</span>
                                                        </div>
                                                    </td>
                                                    <td className="text-end" style={{ padding: '1rem' }}>
                                                        <div style={{
                                                            display: 'inline-block',
                                                            background: '#fee2e2',
                                                            color: '#dc2626',
                                                            padding: '0.5rem 1rem',
                                                            borderRadius: '20px',
                                                            fontWeight: '700',
                                                            fontSize: '1rem'
                                                        }}>
                                                            - Bs. {parseFloat(e.monto).toFixed(2)}
                                                        </div>
                                                    </td>
                                                    <td className="text-center" style={{ padding: '1rem' }}>
                                                        <button 
                                                            className="btn btn-sm"
                                                            onClick={() => handleDelete(e.id, e.descripcion, e.monto)}
                                                            style={{
                                                                background: '#fee2e2',
                                                                color: '#dc2626',
                                                                border: 'none',
                                                                borderRadius: '8px',
                                                                padding: '0.5rem 0.75rem',
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                            onMouseEnter={e => {
                                                                e.target.style.background = '#dc2626';
                                                                e.target.style.color = 'white';
                                                            }}
                                                            onMouseLeave={e => {
                                                                e.target.style.background = '#fee2e2';
                                                                e.target.style.color = '#dc2626';
                                                            }}
                                                        >
                                                            <FaTrash/>
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {egresosFiltrados.length > 0 && (
                        <div className="card-footer bg-light border-0 text-center py-3">
                            <small className="text-muted d-block mb-2">
                                Mostrando {Math.min(limite, egresosFiltrados.length)} de {egresosFiltrados.length} registros de {tituloMes}
                            </small>
                            {egresosFiltrados.length > limite && (
                                <button type="button" className="btn btn-outline-danger" style={{ borderRadius: '20px' }} onClick={() => setLimite(limite + 25)}>
                                    Ver 25 más
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Modal Registrar Gasto - Mejorado */}
            {showModal && (
                <div 
                    className="modal d-block" 
                    style={{ 
                        backgroundColor: 'rgba(0,0,0,0.6)',
                        backdropFilter: 'blur(4px)'
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div 
                        className="modal-dialog modal-dialog-centered modal-lg"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '20px', overflow: 'hidden' }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                padding: '1.5rem'
                            }}>
                                <div className="d-flex align-items-center justify-content-between">
                                    <h5 className="m-0 text-white fw-bold d-flex align-items-center gap-2">
                                        <FaPlus/> Registrar Gasto
                                    </h5>
                                    <button 
                                        className="btn btn-light btn-sm"
                                        onClick={() => setShowModal(false)}
                                        style={{ borderRadius: '50%', width: '32px', height: '32px', padding: 0 }}
                                    >
                                        <FaTimes/>
                                    </button>
                                </div>
                            </div>
                            <form onSubmit={handleSave}>
                                <div className="modal-body p-4">
                                    {/* Switch de compra */}
                                    <div 
                                        className="d-flex align-items-center justify-content-between p-3 mb-4"
                                        style={{
                                            background: esCompra ? 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' : '#f1f5f9',
                                            borderRadius: '12px',
                                            border: esCompra ? '2px solid #f59e0b' : '2px solid #e2e8f0'
                                        }}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <FaShoppingCart style={{ color: esCompra ? '#f59e0b' : '#64748b', fontSize: '1.5rem' }}/>
                                            <div>
                                                <div className="fw-bold" style={{ color: esCompra ? '#92400e' : '#475569' }}>
                                                    ¿Es una compra de insumos?
                                                </div>
                                                <small className="text-muted">Se actualizará el inventario automáticamente</small>
                                            </div>
                                        </div>
                                        <div className="form-check form-switch">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id="checkCompra" 
                                                checked={esCompra} 
                                                onChange={e => { setEsCompra(e.target.checked); limpiarInsumo(); }}
                                                style={{ 
                                                    width: '3rem', 
                                                    height: '1.5rem',
                                                    cursor: 'pointer'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {esCompra ? (
                                        <div 
                                            className="p-3 mb-3"
                                            style={{
                                                background: '#fef3c7',
                                                border: '2px solid #f59e0b',
                                                borderRadius: '12px'
                                            }}
                                        >
                                            <label className="form-label fw-semibold text-muted small mb-2">Destino de la compra</label>
                                            <div className="btn-group w-100 mb-3">
                                                <button type="button" className={`btn ${destinoCompra === 'inventario' ? 'btn-warning fw-bold' : 'btn-outline-warning'}`} onClick={() => cambiarDestino('inventario')}>📋 Inventario</button>
                                                <button type="button" className={`btn ${destinoCompra === 'insumos' ? 'btn-warning fw-bold' : 'btn-outline-warning'}`} onClick={() => cambiarDestino('insumos')}>📦 Insumos (almacén)</button>
                                            </div>

                                            <label className="form-label fw-semibold text-muted small mb-2">
                                                <FaShoppingCart className="me-2"/>
                                                Insumo
                                            </label>
                                            {insumoSel ? (
                                                <div className="d-flex justify-content-between align-items-center p-3 mb-3 bg-white" style={{ borderRadius: '10px', border: '2px solid #f59e0b' }}>
                                                    <div>
                                                        <div className="fw-bold">{insumoSel.nombre_insumo}</div>
                                                        <small className="text-muted">Stock actual: {insumoSel.cantidad} {insumoSel.unidad_medida}</small>
                                                    </div>
                                                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={limpiarInsumo}>Cambiar</button>
                                                </div>
                                            ) : (
                                                <div className="mb-3">
                                                    <input
                                                        type="text"
                                                        name="busqueda_insumo_compra"
                                                        autoComplete="off"
                                                        className="form-control form-control-lg"
                                                        placeholder="Escribe el nombre del insumo..."
                                                        value={busquedaInsumo}
                                                        onChange={e => setBusquedaInsumo(e.target.value)}
                                                        style={{ borderRadius: '10px', border: '2px solid #f59e0b' }}
                                                    />
                                                    {busquedaInsumo.trim() !== '' && (
                                                        <div className="border rounded bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                            {insumos.filter(i => i.nombre_insumo.toLowerCase().includes(busquedaInsumo.trim().toLowerCase())).slice(0, 8).map(i => (
                                                                <div key={i.id} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom" style={{ cursor: 'pointer' }} onClick={() => seleccionarInsumo(i)}>
                                                                    <span>{i.nombre_insumo} <small className="text-muted">(stock: {i.cantidad} {i.unidad_medida})</small></span>
                                                                </div>
                                                            ))}
                                                            {insumos.filter(i => i.nombre_insumo.toLowerCase().includes(busquedaInsumo.trim().toLowerCase())).length === 0 && (
                                                                <div className="px-3 py-2 text-muted small">
                                                                    {insumos.length === 0 && destinoCompra === 'insumos' ? 'No hay insumos en el módulo Insumos. Créalos primero.' : 'Ningún insumo coincide'}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <label className="form-label fw-semibold text-muted small mb-2">
                                                Cantidad Comprada
                                            </label>
                                            <input 
                                                type="number" 
                                                step="0.01"
                                                className="form-control form-control-lg" 
                                                placeholder="0.00"
                                                required
                                                value={form.cantidad_comprada}
                                                onChange={e => setForm({...form, cantidad_comprada: e.target.value})}
                                                style={{ borderRadius: '10px', border: '2px solid #f59e0b' }}
                                            />
                                        </div>
                                    ) : (
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold text-muted small">
                                                <FaFileInvoice className="me-2"/>
                                                Descripción del Gasto
                                            </label>
                                            <input 
                                                className="form-control form-control-lg" 
                                                placeholder="Ej: Pago de luz, agua, mantenimiento..." 
                                                required
                                                value={form.descripcion} 
                                                onChange={e => setForm({...form, descripcion: e.target.value})}
                                                style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                            />
                                        </div>
                                    )}

                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-muted small">
                                            <FaMoneyBillAlt className="me-2"/>
                                            Monto Total (Bs)
                                        </label>
                                        <input 
                                            type="number" 
                                            step="0.50" 
                                            className="form-control form-control-lg" 
                                            placeholder="0.00" 
                                            required 
                                            value={form.monto} 
                                            onChange={e => setForm({...form, monto: e.target.value})}
                                            style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                        />
                                    </div>

                                    {isAlquilacar && (
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold text-muted small">
                                                <FaCar className="me-2"/>
                                                Asignar a Vehículo (Opcional)
                                            </label>
                                            <select 
                                                className="form-select form-select-lg" 
                                                value={form.vehiculo_id} 
                                                onChange={e => setForm({...form, vehiculo_id: e.target.value})}
                                                style={{ borderRadius: '10px', border: '2px solid #e2e8f0' }}
                                            >
                                                <option value="">-- Sin asignar --</option>
                                                {vehiculos.map(v => (
                                                    <option key={v.id} value={v.id}>
                                                        {v.marca} {v.modelo} - {v.placa}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                    <div className="alert alert-info d-flex align-items-start gap-2" style={{ 
                                        borderRadius: '10px',
                                        background: '#dbeafe',
                                        border: 'none',
                                        color: '#1e40af'
                                    }}>
                                        <FaExclamationCircle className="mt-1"/>
                                        <small>
                                            {esCompra 
                                                ? 'Este registro incrementará el stock del insumo seleccionado.'
                                                : 'Este gasto se registrará en el historial de egresos de la sucursal.'
                                            }
                                        </small>
                                    </div>
                                </div>
                                <div className="modal-footer bg-light border-0 p-3">
                                    <button 
                                        type="button" 
                                        className="btn btn-light"
                                        onClick={() => setShowModal(false)}
                                        style={{ borderRadius: '10px', padding: '0.5rem 1.5rem' }}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="btn btn-danger fw-bold"
                                        style={{
                                            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                            border: 'none',
                                            borderRadius: '10px',
                                            padding: '0.5rem 2rem'
                                        }}
                                    >
                                        <FaCheckCircle className="me-2"/> Registrar Gasto
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}